import {
  FidcComparatorOption,
  FidcComparatorSideBySide,
  FidcComparatorTableItem,
} from 'src/services/fidcComparator';

import { MAX_SELECTED_FIDCS } from './constants';

const GENERIC_FIDC_NAME = 'FIDC';
const INITIAL_SELECTED_FIDC_NAME = 'FIDC selecionado';

const isFidcComparatorOption = (
  item: FidcComparatorOption | null,
): item is FidcComparatorOption => item !== null;

const buildClassSeriesLabel = (
  cota: string | null,
  serie: number | null,
  classSerie?: string | null,
) => {
  if (classSerie) return classSerie;

  const parts = [cota];

  if (serie) {
    parts.push(`Série ${serie}`);
  }

  return parts.filter(Boolean).join(' | ');
};

const buildComparatorValue = ({
  operationId,
  cota,
  serie,
}: {
  operationId: number | string;
  cota: string | null;
  serie: number | null;
}) => {
  if (!cota && serie === null) return String(operationId);

  return `${operationId}:${cota ?? ''}:${serie ?? ''}`;
};

export const formatSelectedFidcLabel = ({
  name,
  cota,
  serie,
  classSerie,
}: {
  name?: string | null;
  cota: string | null;
  serie: number | null;
  classSerie?: string | null;
}) => {
  const resolvedName = name?.trim() || GENERIC_FIDC_NAME;
  const resolvedClassSerie = buildClassSeriesLabel(cota, serie, classSerie);

  return resolvedClassSerie
    ? `${resolvedName} | ${resolvedClassSerie}`
    : resolvedName;
};

export const parseInitialItems = (itemsParam: string): FidcComparatorOption[] =>
  itemsParam
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
    .slice(0, MAX_SELECTED_FIDCS)
    .map((value): FidcComparatorOption | null => {
      const [operationId, cota, serie] = value.split(':');
      const normalizedOperationId = Number(operationId);
      const normalizedCota = cota?.trim() || null;
      const parsedSerie =
        serie === undefined || serie === '' ? null : Number(serie);

      if (
        !Number.isInteger(normalizedOperationId) ||
        normalizedOperationId <= 0 ||
        (serie !== undefined && !Number.isInteger(parsedSerie))
      ) {
        return null;
      }

      return {
        value: buildComparatorValue({
          operationId: normalizedOperationId,
          cota: normalizedCota,
          serie: parsedSerie,
        }),
        operationId: normalizedOperationId,
        name: INITIAL_SELECTED_FIDC_NAME,
        cnpj: '',
        cota: normalizedCota,
        serie: parsedSerie,
        label: formatSelectedFidcLabel({
          name: GENERIC_FIDC_NAME,
          cota: normalizedCota,
          serie: parsedSerie,
        }),
      };
    })
    .filter(isFidcComparatorOption);

const buildSelectionMatchKey = ({
  operationId,
  cota,
  serie,
}: {
  operationId: number | string;
  cota: string | null;
  serie: number | null;
}) => `${operationId}|${cota ?? ''}|${serie ?? ''}`;

const isFundOnlySelection = (item: FidcComparatorOption) =>
  !item.cota && item.serie === null;

export const reconcileSelectedFidcsWithSideBySide = (
  selectedFidcs: FidcComparatorOption[],
  sideBySide: FidcComparatorSideBySide[],
) => {
  const sideBySideByKey = new Map(
    sideBySide.map((item) => [buildSelectionMatchKey(item), item]),
  );
  const sideBySideByOperationId = new Map(
    sideBySide.map((item) => [item.operationId, item]),
  );

  let changed = false;

  const nextSelectedFidcs = selectedFidcs.map((item) => {
    const isFundOnly = isFundOnlySelection(item);
    const match = isFundOnly
      ? sideBySideByOperationId.get(item.operationId)
      : sideBySideByKey.get(buildSelectionMatchKey(item));

    if (!match?.name) {
      return item;
    }

    const nextName = match.name;
    const nextCnpj = match.cnpj ?? item.cnpj;
    const nextLabel = formatSelectedFidcLabel({
      name: nextName,
      cota: item.cota,
      serie: item.serie,
      classSerie: isFundOnly ? null : match.classSerie,
    });

    if (
      item.name === nextName &&
      item.cnpj === nextCnpj &&
      item.label === nextLabel
    ) {
      return item;
    }

    changed = true;

    return {
      ...item,
      name: nextName,
      cnpj: nextCnpj,
      label: nextLabel,
    };
  });

  return changed ? nextSelectedFidcs : selectedFidcs;
};

export const reconcileSelectedFidcsWithTableData = (
  selectedFidcs: FidcComparatorOption[],
  tableData: FidcComparatorTableItem[],
) => {
  const tableDataByOperationId = new Map(
    tableData.map((item) => [String(item.operationId), item]),
  );

  let changed = false;

  const nextSelectedFidcs = selectedFidcs.map((item) => {
    if (!isFundOnlySelection(item)) {
      return item;
    }

    const match = tableDataByOperationId.get(String(item.operationId));

    if (!match) {
      return item;
    }

    const matchedName = match.name?.trim() || match.fidc?.trim();
    const matchedCnpj = match.cnpj?.trim();
    const nextName = matchedName || item.name;
    const nextCnpj = matchedCnpj || item.cnpj;
    const nextLabel = formatSelectedFidcLabel({
      name: nextName,
      cota: item.cota,
      serie: item.serie,
    });

    if (
      item.name === nextName &&
      item.cnpj === nextCnpj &&
      item.label === nextLabel
    ) {
      return item;
    }

    changed = true;

    return {
      ...item,
      name: nextName,
      cnpj: nextCnpj,
      label: nextLabel,
    };
  });

  return changed ? nextSelectedFidcs : selectedFidcs;
};

export const orderFidcComparisonDataBySelection = (
  selectedFidcs: FidcComparatorOption[],
  tableData: FidcComparatorTableItem[],
): FidcComparatorTableItem[] => {
  const tableDataByOperationId = new Map(
    tableData.map((item) => [String(item.operationId), item]),
  );

  return selectedFidcs
    .map((selection) =>
      tableDataByOperationId.get(String(selection.operationId)),
    )
    .filter((item): item is FidcComparatorTableItem => item !== undefined);
};
